import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { UserPlus, AtSign, UserX, FileSpreadsheet, X } from "lucide-react";
import StaffOnboardingWizard from "../components/StaffOnboardingWizard";
import UserTable from "./users/UserTable";
import UserFilters from "./users/UserFilters";
import UserFormModal from "./users/UserFormModal";
import FormerStaffModal from "./users/FormerStaffModal";

const blankUser = () => ({
  email: "", full_name: "", password: "", access_role: "Staff",
  job_title: "", assigned_home: "", assigned_homes: [], gender: "",
  staff_classification: "", active_status: true,
  // Backend derives the default from access_role when this is omitted, but we set
  // the initial UI state explicitly so the toggle reflects the resolver rule.
  receive_monthly_kpi_digest: false,
});

export default function UserManagementPage() {
  const { user, hasCapability, refresh } = useAuth();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [emailEditing, setEmailEditing] = useState(null); // { user_id, full_name, email, new_email, saving }
  const [status, setStatus] = useState("current");
  const [selected, setSelected] = useState(() => new Set());
  const [formerModal, setFormerModal] = useState(null); // { people, source }
  const [formerBusy, setFormerBusy] = useState(false);
  const [preloadCounts, setPreloadCounts] = useState(null);

  const load = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);

  const canManageFormer = ["CEO", "Project Manager", "COO", "Administration"].includes(user.access_role);
  useEffect(() => {
    if (canManageFormer) api.get("/former-staff/preload").then(({ data }) => setPreloadCounts(data.counts)).catch(() => {});
  }, [canManageFormer, users]);
  const canDelete = ["CEO", "Project Manager", "COO", "Administration"].includes(user.access_role);
  const canEdit = ["CEO", "Project Manager", "COO", "Administration"].includes(user.access_role);
  const canCreate = ["CEO", "Project Manager", "COO", "Administration"].includes(user.access_role);
  // HM can onboard new staff if their permission matrix grants User Management → Create.
  // The "Add User" + delete + edit privileges remain limited to CEO/PM/COO/Admin.
  const canOnboard = canCreate || (user.access_role === "House Manager" && hasCapability("User Management", "Create"));
  const canResetPwd = ["CEO", "Project Manager", "COO", "Administration"].includes(user.access_role);
  const canResetDemo = ["CEO", "Project Manager", "COO"].includes(user.access_role);
  // Only CEO / PM / COO can toggle the monthly-digest permission on other users.
  // Self-toggle is blocked at the backend regardless.
  const canManageDigestPermission = ["CEO", "Project Manager", "COO"].includes(user.access_role);
  const canEditUserEmail = hasCapability("User Management", "Edit User Email");
  const canEditSelfEmail = hasCapability("User Management", "Edit Self Email");

  const onResetPwd = async (target) => {
    if (!window.confirm(`Send a password-reset email to ${target.full_name} (${target.email})?\n\nThe link expires in 30 minutes.`)) return;
    try {
      const { data } = await api.post(`/users/${target.user_id}/reset-password`);
      if (data.email_sent) toast.success(`Reset email sent to ${data.to}`);
      else toast.warning(`Token created — but email could not be sent. Check Resend config.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset email");
    }
  };

  const onSaveEmail = async () => {
    if (!emailEditing) return;
    const next = (emailEditing.new_email || "").trim().toLowerCase();
    if (!next || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      return toast.error("Enter a valid email address.");
    }
    if (next === (emailEditing.email || "").toLowerCase()) {
      setEmailEditing(null);
      return toast("Email unchanged.");
    }
    if (!window.confirm(
      `Change ${emailEditing.full_name}'s email\n\n  From: ${emailEditing.email}\n  To:   ${next}\n\nThis will also change their LOGIN USERNAME.\nProceed?`
    )) return;
    setEmailEditing({ ...emailEditing, saving: true });
    try {
      await api.patch(`/users/${emailEditing.user_id}/email`, { new_email: next });
      toast.success("Email updated");
      setEmailEditing(null);
      load();
      // If we changed our own email, refresh the auth context too
      if (emailEditing.user_id === user.user_id) refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update email");
      setEmailEditing({ ...emailEditing, saving: false });
    }
  };

  const onResetDemo = async () => {
    const phrase = window.prompt(
      "This wipes ALL KPI scores, monthly summaries, and QA audits.\n" +
      "Users, staff, homes, permissions, and audit logs are kept.\n\n" +
      'Type RESET (in caps) to confirm:'
    );
    if (!phrase) return;
    try {
      const { data } = await api.post("/admin/reset-demo-data", { confirm: phrase.trim() });
      const lines = Object.entries(data.deleted).map(([k, v]) => `${k}: ${v}`).join(" · ");
      toast.success(`Demo data wiped (${lines})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const onBulkReimport = () => { window.location.assign("/users/import"); };

  const isFormer = (u) => u.employment_status === "former";
  const statusCounts = {
    all: users.length, former: users.filter(isFormer).length,
    current: users.filter((u) => !isFormer(u)).length,
    active: users.filter((u) => !isFormer(u) && u.active_status).length,
    inactive: users.filter((u) => !isFormer(u) && !u.active_status).length,
  };
  const filtered = users.filter((u) => {
    if (status === "current" && isFormer(u)) return false;
    if (status === "active" && (isFormer(u) || !u.active_status)) return false;
    if (status === "inactive" && (isFormer(u) || u.active_status)) return false;
    if (status === "former" && !isFormer(u)) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) ||
           (u.assigned_home || "").toLowerCase().includes(q) || (u.access_role || "").toLowerCase().includes(q);
  });

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (rows) => setSelected((s) => {
    const all = rows.every((u) => s.has(u.user_id));
    return new Set(all ? [] : rows.map((u) => u.user_id));
  });

  const openFormerForSelection = () => {
    const people = users.filter((u) => selected.has(u.user_id)).map((u) => ({ ...u, matched: true, already_former: isFormer(u), blocked: null }));
    setFormerModal({ people, source: "" });
  };
  const openFormerPreload = async () => {
    try {
      const { data } = await api.get("/former-staff/preload");
      setFormerModal({ people: data.people, source: data.source });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the spreadsheet list"); }
  };
  const onConfirmFormer = async (payload) => {
    setFormerBusy(true);
    try {
      const { data } = await api.post("/former-staff/mark", payload);
      const c = data.counts;
      toast.success(`${c.marked} marked as Former Staff${c.blocked ? ` · ${c.blocked} blocked` : ""}${c.already_former ? ` · ${c.already_former} already former` : ""}`);
      if (c.blocked) data.report.blocked.forEach((b) => toast.warning(`${b.full_name}: ${b.reason}`));
      setFormerModal(null); setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark as Former Staff"); }
    finally { setFormerBusy(false); }
  };
  const onReinstate = async (u) => {
    if (!window.confirm(`Reinstate ${u.full_name}?\n\nTheir login is re-enabled and they return to the Staff Directory and pickers with their previous role and home(s).`)) return;
    try {
      const { data } = await api.post("/former-staff/reinstate", { user_ids: [u.user_id] });
      if (data.counts.reinstated) toast.success(`${u.full_name} reinstated`);
      else toast.warning(data.report.blocked?.[0]?.reason || "Nothing changed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reinstate"); }
  };

  const onDeactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.full_name}?`)) return;
    try { await api.post(`/users/${u.user_id}/deactivate`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onDelete = async (u) => {
    if (!window.confirm(`PERMANENTLY delete ${u.full_name}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onSave = async () => {
    if (!editing) return;
    try {
      const payload = { ...editing };
      delete payload.user_id; delete payload.email;
      if (!payload.password) delete payload.password;
      await api.patch(`/users/${editing.user_id}`, payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onCreate = async () => {
    if (!creating) return;
    if (!creating.email || !creating.full_name || !creating.password || !creating.access_role) {
      return toast.error("Email, Full name, Password and Access role are required.");
    }
    try {
      await api.post("/users", creating);
      toast.success(`${creating.full_name} created`);
      setCreating(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create"); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-small-caps">Users & Access</p>
          <h1 className="text-h1 mt-1">User Management</h1>
          <p className="text-sm text-slate-500 mt-1">{users.length} accounts · imported from bulk template</p>
        </div>
        <UserFilters
          filter={filter} onFilterChange={setFilter}
          status={status} onStatusChange={(v) => { setStatus(v); setSelected(new Set()); }} statusCounts={statusCounts}
          canBulkImport={canResetDemo} onBulkReimport={onBulkReimport}
          canResetDemo={canResetDemo} onResetDemo={onResetDemo}
          canOnboard={canOnboard} onOnboard={() => setShowWizard(true)}
          canCreate={canCreate} onCreate={() => setCreating(blankUser())}
        />
      </div>

      {canManageFormer && (
        <div className="flex flex-wrap items-center gap-2" data-testid="former-staff-toolbar">
          <button onClick={openFormerPreload} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-rose-200 hover:bg-rose-50 text-rose-700 text-sm font-semibold bg-white" data-testid="former-preload-btn" title="Review the names from the Image Staff spreadsheet">
            <FileSpreadsheet size={14} /> Review spreadsheet list{preloadCounts ? ` (${preloadCounts.matched - preloadCounts.already_former} of ${preloadCounts.total} still to mark)` : ""}
          </button>
          {selected.size > 0 && (
            <>
              <button onClick={openFormerForSelection} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-rose-700 hover:bg-rose-800 text-white text-sm font-semibold" data-testid="former-mark-selected-btn">
                <UserX size={14} /> Mark {selected.size} as Former Staff
              </button>
              <button onClick={() => setSelected(new Set())} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="former-clear-selection-btn"><X size={14} /> Clear</button>
            </>
          )}
          {selected.size === 0 && <span className="text-sm text-slate-500">Tick people in the table to mark them as Former Staff, or review the spreadsheet list.</span>}
        </div>
      )}

      <UserTable
        users={filtered} currentUserId={user.user_id}
        canEdit={canEdit} canDelete={canDelete} canResetPwd={canResetPwd} canManageFormer={canManageFormer}
        canEditUserEmail={canEditUserEmail} canEditSelfEmail={canEditSelfEmail}
        selected={selected} onToggleSelect={toggleSelect} onToggleAll={toggleAll}
        onEdit={(u) => setEditing({ ...u, password: "" })}
        onEditEmail={(u) => setEmailEditing({ user_id: u.user_id, full_name: u.full_name, email: u.email, new_email: u.email, saving: false })}
        onResetPwd={onResetPwd}
        onDeactivate={onDeactivate}
        onDelete={onDelete}
        onReinstate={onReinstate}
      />

      {formerModal && (
        <FormerStaffModal people={formerModal.people} source={formerModal.source} busy={formerBusy}
          onCancel={() => setFormerModal(null)} onConfirm={onConfirmFormer} />
      )}

      <UserFormModal
        mode="create" value={creating} onChange={setCreating}
        onCancel={() => setCreating(null)} onSubmit={onCreate}
        canManageDigestPermission={canManageDigestPermission}
        currentUserId={user.user_id}
      />

      {showWizard && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setShowWizard(false)}>
          <div className="nv-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="onboard-modal">
            <h2 className="text-h2 inline-flex items-center gap-2"><UserPlus size={18} className="text-emerald-700" /> Staff Onboarding</h2>
            <p className="text-sm text-slate-500 mt-1 mb-4">Guided 4-step new-user creator. Temporary password is auto-generated and emailed.</p>
            <StaffOnboardingWizard onClose={() => setShowWizard(false)} onCreated={load} />
          </div>
        </div>
      )}

      <UserFormModal
        mode="edit" value={editing} onChange={setEditing}
        onCancel={() => setEditing(null)} onSubmit={onSave}
        canManageDigestPermission={canManageDigestPermission}
        currentUserId={user.user_id}
      />

      {emailEditing && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => !emailEditing.saving && setEmailEditing(null)}>
          <div className="nv-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-email-modal">
            <div className="flex items-center gap-2 mb-2">
              <AtSign size={18} className="text-indigo-700" />
              <h2 className="text-h2">Change login email</h2>
            </div>
            <p className="text-sm text-slate-600">
              You're updating <span className="font-semibold text-slate-800">{emailEditing.full_name}</span>'s account.
            </p>
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              <strong>Heads up:</strong> changing this email will also change this user's <strong>login username</strong>. They will need to use the new email on their next sign-in.
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <span className="text-small-caps">Current email</span>
                <div className="mt-1 text-sm text-slate-700 bg-slate-50 rounded-md px-3 py-2 border border-slate-200" data-testid="edit-email-current">{emailEditing.email}</div>
              </div>
              <label className="block">
                <span className="text-small-caps">New email</span>
                <input
                  type="email" autoFocus
                  value={emailEditing.new_email}
                  onChange={(e) => setEmailEditing({ ...emailEditing, new_email: e.target.value })}
                  className="mt-1 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                  data-testid="edit-email-input"
                />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setEmailEditing(null)} disabled={emailEditing.saving}
                className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                data-testid="edit-email-cancel-btn"
              >Cancel</button>
              <button
                onClick={onSaveEmail} disabled={emailEditing.saving}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-indigo-700 hover:bg-indigo-800 text-white disabled:opacity-60"
                data-testid="edit-email-save-btn"
              >{emailEditing.saving ? "Saving…" : "Save email"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
